<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="flex aspect-square rounded-full overflow-hidden items-center justify-center bg-gray-50 border border-gray-200 relative flex-1 container-type-size">
    <CarvisSpinner
      v-show="loading"
      class="absolute"
    />
    <img
      v-if="avatarImgUrl"
      alt="Avatar"
      class="h-full w-full rounded-full object-cover"
      :src="avatarImgUrl"
      @load="loading = false"
    >
    <div
      v-else-if="avatarSvg"
      alt="Avatar"
      class="h-full w-full rounded-full object-cover"
      v-html="avatarSvg"
    />
    <div
      v-else-if="!loading && fallbackInitials"
      class="w-full h-full flex items-center justify-center text-white font-bold select-none"
      :style="{ backgroundColor: fallbackColor, fontSize: 'clamp(0.6rem, 40cqi, 1.5rem)' }"
    >
      {{ fallbackInitials }}
    </div>
  </div>
</template>

<script setup lang="ts">
import multiavatar from '@multiavatar/multiavatar/esm'
import { computed, onMounted, ref, watch } from 'vue'

import { useImagesStore } from '@/stores/images-store'
import { useToastsStore } from '@/stores/toasts-store'

export type AvatarFallback =
  | { seed: string; initials?: never }
  | { initials: string; seed?: never }

const props = defineProps<{
  avatarImageId: string | null
  imageIdOverride?: string
  fallback?: AvatarFallback
}>()

const loading = ref(false)
const avatarImgUrl = ref<string | null>(null)
const avatarSvg = ref<string | null>(null)

const toastsStore = useToastsStore()
const imagesStore = useImagesStore()

const fallbackInitials = computed(() => {
  if (!props.fallback || !('initials' in props.fallback) || !props.fallback.initials) return null
  const words = props.fallback.initials.trim().split(/\s+/)
  return words.slice(0, 2).map(w => w[0]?.toUpperCase()).filter(Boolean).join('')
})

const fallbackColor = computed(() => {
  if (!props.fallback || !('initials' in props.fallback) || !props.fallback.initials) return undefined
  let hash = 0
  for (const char of props.fallback.initials) {
    hash = char.charCodeAt(0) + ((hash << 5) - hash)
  }
  const hue = ((hash % 360) + 360) % 360
  return `hsl(${hue}, 45%, 55%)`
})

const loadAvatarFromImageId = async (imageId: string): Promise<void> => {
  loading.value = true
  try {
    const newUrl = await imagesStore.fetchAvatarImageUrl(imageId, { size: 200, resize: 'contain' })
    if (newUrl === avatarImgUrl.value) {
      // URL didn't change, browser won't fire @load event, so clear loading manually
      loading.value = false
    } else {
      avatarImgUrl.value = newUrl
    }
  } catch (error) {
    avatarImgUrl.value = null
    loading.value = false
    toastsStore.error('Fehler beim Laden des Avatars', 'Beim Laden des Avatars ist ein Fehler aufgetreten. Bitte versuche es erneut.', error as Error)
  }
}

const resetAvatarState = (): void => {
  avatarImgUrl.value = null
  avatarSvg.value = null
  loading.value = false
}

const loadAvatar = (): void => {
  if (props.imageIdOverride) {
    avatarSvg.value = null
    void loadAvatarFromImageId(props.imageIdOverride)
    return
  }

  if (props.avatarImageId) {
    avatarSvg.value = null
    void loadAvatarFromImageId(props.avatarImageId)
    return
  }

  // No image available - show fallback
  resetAvatarState()
  const seed = props.fallback && 'seed' in props.fallback ? props.fallback.seed : undefined
  if (seed) {
    avatarSvg.value = multiavatar(seed)
  }
}

watch(() => props.avatarImageId, loadAvatar)
watch(() => props.imageIdOverride, loadAvatar)
watch(() => props.fallback, loadAvatar, { deep: true })
onMounted(() => loadAvatar())
</script>

<style scoped>
.container-type-size {
  container-type: inline-size;
}
</style>
